import { BrowserRouter, Navigate, Route, Routes, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom';
import LoadingSpinner from '@/components/common/elements/LoadingSpinner';
import PageViewTracker from '@/components/common/PageViewTracker';
import PullToRefresh, { SOFT_REFRESH_EVENT } from '@/components/common/PullToRefresh';
import SwipeBack from '@/components/common/SwipeBack';
import AiSheetHost from '@/pages/AiChat/components/AiSheetHost';
import { useAiBackgroundLocation } from '@/pages/AiChat/aiSheetRoute';
import PhoneNumberPrompt from '@/components/common/PhoneNumberPrompt';
import OpenInAppSheet from '@/components/common/OpenInAppSheet';
import { Container, CssBaseline, GlobalStyles, ThemeProvider } from '@mui/material';
import { globalStyles } from './styles/globalStyles';
import { theme } from './styles/theme';
import { CONTENT_MAX_WIDTH, CONTENT_MIN_WIDTH, CONTENT_PADDING_X } from './styles/layout';
import Home from './pages/Home';
import { useGetHeaderHeight, useIsMobile } from './common/hooks';
import Signup from './pages/Signup';
import Login from './pages/Login';
import SignupSuccess from './pages/Signup/SignupSuccess';
import Notice from '@/pages/Notice';
import Post from '@/pages/Post';
import TermsOfService from '@/pages/Legal/TermsOfService';
import PrivacyPolicy from '@/pages/Legal/PrivacyPolicy';
import RefundPolicy from '@/pages/Legal/RefundPolicy';
import PageState from '@/components/common/PageState';
import Header from '@/components/layout/Header';
import AppInstallBanner from '@/components/layout/AppInstallBanner';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import RegionRecruitments from "./pages/Recruitments/RecruitmentList";
import WelfareRecruitmentList from "./pages/Recruitments/WelfareRecruitmentList";
import Footer from '@/components/common/elements/Footer';
import PageBannerSlot from '@/components/common/banner/PageBannerSlot';
import Toast from './components/common/elements/Toast';
import { SeminarList } from './pages/Seminar';
import { navigationService } from './common/navigationService';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { isLoggedInAtom } from '@/state/loginState';
import { registerPushNotifications } from '@/native/capacitorApp';
import { flushPendingDeepLink } from '@/native/deepLink';
import { clearListSnapshots } from '@/common/listRestore';
import { toastState } from './state/toastState';
import ProtectedRoute from './components/common/ProtectedRoute';
import PointFeatureRoute from './components/common/PointFeatureRoute';
import SeminarPaymentFeatureRoute from './components/common/SeminarPaymentFeatureRoute';

/** /notices — `?tab=faq` 면 로그인 없이, 공지 탭은 로그인 필요 */
const NoticeRoute = () => {
    const [params] = useSearchParams();
    return params.get('tab') === 'faq' ? <Notice /> : <ProtectedRoute><Notice /></ProtectedRoute>;
};

// Lazy-loaded pages

const PasswordReset = lazy(() => import('./pages/PasswordReset/PasswordReset'));
const ClinicEmailChangePage = lazy(() => import('./pages/Signup/ClinicEmailChangePage'));
const AppBridgePage = lazy(() => import('./pages/AppBridge/AppBridgePage'));
const MyPage = lazy(() => import('./pages/MyPage'));
const PostForm = lazy(() => import("@/pages/Post/PostForm"));
const PostDetail = lazy(() => import("@/pages/Post/PostDetail"));
const MyPosts = lazy(() => import('@/pages/Post/MyPosts'));
const InquiryFormPage = lazy(() => import('@/pages/Inquiry/InquiryFormPage'));
const PrizePage = lazy(() => import('@/pages/Prize/PrizePage'));
const MyInquiriesPage = lazy(() => import('@/pages/Inquiry/MyInquiriesPage'));
const InquiryThreadPage = lazy(() => import('@/pages/Inquiry/InquiryThreadPage'));
const RegisterResume = lazy(() => import('./pages/Resume'));
const ResumeViewPage = lazy(() => import('./pages/Resume/ResumeViewPage'));
const ResumeEditPage = lazy(() => import('./pages/Resume/ResumeEditPage'));
const RecruitmentsDetail = lazy(() => import('./pages/Recruitments/RecruitmentDetail'));
const RecruitmentCreation = lazy(() => import('./pages/Recruitments/RecruitmentForm'));
const RecruitmentSurveyPage = lazy(() => import('./pages/Recruitments/survey/RecruitmentSurveyPage'));
const ApplicationResumeView = lazy(() => import('./pages/Recruitments/ApplicationResumeView'));
const ApplicationManagementPage = lazy(() => import('./pages/Applications/ApplicationManagementPage'));
const NoticeDetail = lazy(() => import("@/pages/Notice/NoticeDetail"));
const SeminarForm = lazy(() => import('./pages/Seminar/SeminarForm'));
const SeminarDetail = lazy(() => import('./pages/Seminar/SeminarDetail'));
const SeminarPaymentCallback = lazy(() => import('./pages/Seminar/SeminarPaymentCallback'));
const ExpertLounge = lazy(() => import('./pages/ExpertLounge/ExpertLounge'));
const ExpertLoungeForm = lazy(() => import('./pages/ExpertLounge/ExpertLoungeForm'));
const ExpertLoungeDetail = lazy(() => import('./pages/ExpertLounge/ExpertLoungeDetail'));
const SeminarHistory = lazy(() => import('./pages/Seminar/SeminarHistory'));
const SeminarParticipants = lazy(() => import('./pages/Seminar/SeminarParticipants'));
const InstructorApplication = lazy(() => import('./pages/Seminar/SeminarInstructor/InstructorApplication'));
const InstructorEdit = lazy(() => import('./pages/Seminar/SeminarInstructor/InstructorEdit'));
const InstructorSettlementPage = lazy(() => import('./pages/Seminar/SeminarInstructor/InstructorSettlementPage'));
const InstructorDetail = lazy(() => import('./pages/Instructor/InstructorDetail'));
const ApplyHistoryPage = lazy(() => import('./pages/ApplyHistory/ApplyHistoryPage'));
const ApplicationThreadPage = lazy(() => import('./pages/ApplicationThread/ApplicationThreadPage'));
const ThreadListPage = lazy(() => import('./pages/ApplicationThread/ThreadListPage'));
const ClinicInfoEditPage = lazy(() => import('./pages/MyPage/ClinicInfoEditPage'));
const UserInfoEditPage = lazy(() => import('./pages/MyPage/UserInfoEditPage'));
const NotificationSettingsPage = lazy(() => import('./pages/MyPage/NotificationSettingsPage'));
const PromiseRecordRedirect = lazy(() => import('./pages/MyPage/PromiseRecordRedirect'));
const LicenseVerificationPage = lazy(() => import('./pages/MyPage/LicenseVerificationPage'));
const SalarySurveyLandingPage = lazy(() => import('./pages/SalarySurvey/SalarySurveyLandingPage'));
const SalarySurveyFormPage = lazy(() => import('./pages/SalarySurvey/SalarySurveyFormPage'));
const SalarySurveySubmittedPage = lazy(() => import('./pages/SalarySurvey/SalarySurveySubmittedPage'));
const SalarySurveyResultPage = lazy(() => import('./pages/SalarySurvey/SalarySurveyResultPage'));
const SalaryReportPage = lazy(() => import('./pages/SalarySurvey/SalaryReportPage'));
const ClinicRecruitmentsPage = lazy(() => import('./pages/MyPage/ClinicRecruitmentsPage'));
const SearchPage = lazy(() => import('./pages/Search/SearchPage'));
const ProductPurchasePage = lazy(() => import('./pages/Products/ProductPurchasePage'));
const PurchaseHistoryPage = lazy(() => import('./pages/PurchaseHistory/PurchaseHistoryPage'));
const PaymentHistoryPage = lazy(() => import('./pages/PaymentHistory/PaymentHistoryPage'));
const MyCouponsPage = lazy(() => import('./pages/Coupons/MyCouponsPage'));
const FavoriteClinicsPage = lazy(() => import('./pages/FavoriteClinics/FavoriteClinicsPage'));
const PointChargePage = lazy(() => import('./pages/Points/PointChargePage'));
const NaverCallback = lazy(() => import('./pages/Login/NaverCallback'));
const KakaoCallback = lazy(() => import('./pages/Login/KakaoCallback'));
const AppleCallback = lazy(() => import('./pages/Login/AppleCallback'));
const HomeV2 = lazy(() => import('./pages/HomeV2/HomeV2'));
const NotificationsPage = lazy(() => import('./pages/Notifications/NotificationsPage'));
const AiChatPage = lazy(() => import('./pages/AiChat/AiChatPage'));
// 개발 전용 검증 라우트 — lazy()/import() 호출 자체를 DEV 삼항식 안에 둬야
// Vite/Rollup이 프로덕션 빌드에서 import.meta.env.DEV 를 리터럴 false 로 치환한 뒤
// 죽은 브랜치(= import() 포함)를 통째로 제거해 운영 번들에 별도 청크가 생기지 않는다.
// (바깥에서 lazy()만 감싸고 import()는 항상 평가되는 형태로 두면 트리쉐이킹되지 않는다.)
const NativeNavTest = import.meta.env.DEV
    ? lazy(() => import('./pages/Dev/NativeNavTest'))
    : (() => null);

/**
 * 라우트 이동 시 스크롤을 맨 위로. 상세 페이지 하단에서 링크(예: 강사 프로필)로 이동하면
 * 새 페이지가 이전 스크롤 위치에 열리는 문제 방지. 뒤로가기(POP)는 브라우저 복원에 맡긴다.
 */
function ScrollToTopOnNavigate() {
    // 폰 AI 시트가 열리고 닫혀도(시트 칸) 뒤 화면 스크롤은 그대로 — 아래 깔린 화면의 주소로 판단한다
    const { pathname } = useAiBackgroundLocation();
    const navigationType = useNavigationType();
    const previousPathRef = useRef<string | null>(null);

    useEffect(() => {
        // 화면(pathname)이 실제로 바뀌었을 때만 올린다. 목록 화면이 필터를 URL 에 맞추느라 같은 화면에서 replace 를 하면
        // navigationType 만 POP→REPLACE 로 바뀌는데, 그때 맨 위로 올리면 뒤로가기로 복원한 스크롤 위치가 날아간다.
        const pathChanged: boolean = previousPathRef.current !== pathname;
        previousPathRef.current = pathname;
        if (pathChanged && navigationType !== 'POP') {
            window.scrollTo(0, 0);
        }
    }, [pathname, navigationType]);

    return null;
}

// Navigation service 초기화를 위한 내부 컴포넌트
function NavigationInitializer() {
    const navigate = useNavigate();
    const setToast = useSetRecoilState(toastState);

    useEffect(() => {
        navigationService.setNavigate(navigate);
        navigationService.setToastSetter(setToast);
        // 라우터 준비 완료 — 앱이 딥링크로 콜드 스타트했다면 여기서 해당 화면으로 이동한다
        flushPendingDeepLink();
    }, [navigate, setToast]);

    return null;
}

/**
 * Capacitor 앱에서 로그인 상태가 되면 OS 알림 권한을 요청하고 FCM 토큰을 서버에 등록한다.
 * 웹 브라우저에서는 registerPushNotifications 가 즉시 return 하므로 무해하다.
 */
/** 로그인 상태가 바뀌면 뒤로가기 복원용 목록 저장본을 버린다 — 계정에 따라 목록 내용(차단·관심·내 글)이 다르다 */
function ListSnapshotAuthSync() {
    const isLoggedIn = useRecoilValue(isLoggedInAtom);
    const previousRef = useRef<boolean | null>(null);

    useEffect(() => {
        if (previousRef.current !== null && previousRef.current !== isLoggedIn) {
            clearListSnapshots();
        }
        previousRef.current = isLoggedIn;
    }, [isLoggedIn]);

    return null;
}

function PushRegistrar() {
    const isLoggedIn = useRecoilValue(isLoggedInAtom);

    useEffect(() => {
        if (!isLoggedIn) return;
        registerPushNotifications().catch((error) => console.error('push 등록 실패:', error));
    }, [isLoggedIn]);

    return null;
}

function AppContent() {
    const isMobile = useIsMobile();
    // 폰 AI 시트 칸이면 본문은 아래 깔린 화면을 그대로 그린다(뒤 화면 유지, pages/AiChat/aiSheetRoute.ts)
    const routesLocation = useAiBackgroundLocation();
    const height = useGetHeaderHeight();
    const [applyList, setApplyList] = useState([]);
    const [isReload, setIsReload] = useState(true);
    // 풀투리프레시: 문서 전체를 다시 불러오면(reload) 흰 화면 깜박임이 생기므로, 현재 라우트만 remount 해 데이터를 다시 받는다
    const [refreshKey, setRefreshKey] = useState<number>(0);
    useEffect(() => {
        const onRefresh = (): void => setRefreshKey((key: number) => key + 1);
        window.addEventListener(SOFT_REFRESH_EVENT, onRefresh);
        return () => window.removeEventListener(SOFT_REFRESH_EVENT, onRefresh);
    }, []);

    return (
        <>
            <NavigationInitializer />
            <ScrollToTopOnNavigate />
            <PushRegistrar />
            <ListSnapshotAuthSync />
            {/* 모든 화면 공통 풀투리프레시 (터치 입력에서만 동작) */}
            <PullToRefresh />
            {/* 왼쪽 가장자리 스와이프 뒤로가기 (iOS·Android 앱, 로컬 dev 는 터치 기기) */}
            <SwipeBack />
            <PhoneNumberPrompt />
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <GlobalStyles styles={globalStyles} />
                <AppInstallBanner />
                <Header />
                <Container
                    component="main"
                    id="app-content"
                    sx={{
                        width: '100%',
                        minHeight: `calc(100vh - ${height})`,
                        maxWidth: CONTENT_MAX_WIDTH,
                        minWidth: { xs: 'unset', md: CONTENT_MIN_WIDTH },
                        margin: '0 auto',
                        padding: { xs: '0 0.5rem', md: `0 ${CONTENT_PADDING_X}` },
                        // 모바일 하단 탭바(약 74px + iOS 홈 인디케이터 safe-area) 여백은 Footer가 담당(푸터가 모바일에도 노출됨).
                        // 본문과 푸터 사이 최소 간격만 둔다.
                        paddingBottom: { xs: '16px', md: '0' }
                    }}
                    maxWidth={false}
                    disableGutters
                >
                    {/* 페이지 상단 배너 — 자리를 직접 두는 화면(목록·홈)과 빠지는 화면(결제·폼·로그인·AI)은 비킨다(pageBannerRoutes.ts) */}
                    <PageBannerSlot placement="top" variant="app" />
                    <Suspense fallback={<LoadingSpinner placement="page" />}>
                    <Routes key={refreshKey} location={routesLocation}>
                        {/* ===== 공개(비로그인 허용): 홈 + 인증 플로우만 ===== */}
                        <Route path="/" element={<HomeV2 />} />
                        {/* 앱 다운로드 브릿지 — 카페·메일·QR 의 단일 링크. 기기별 스토어로 이동, PC 는 배지+QR 안내 */}
                        <Route path="/app" element={<AppBridgePage />} />
                        <Route path="/home-v1" element={<Home />} />
                        <Route path="/home-v2" element={<HomeV2 />} />
                        <Route path="/recruitments" element={<HomeV2 />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/password-reset" element={<PasswordReset />} />
                        <Route path="/signup" element={<Signup />} />
                        <Route path="/signup/success" element={<SignupSuccess />} />
                        {/* 이미 가입된 치과 — 가입 이메일 바꾸기 신청(가입 화면 값은 라우터 상태로만 받는다) */}
                        <Route path="/signup/clinic/email-change" element={<ClinicEmailChangePage />} />
                        <Route path="/login/callback/naver" element={<NaverCallback />} />
                        <Route path="/login/callback/kakao" element={<KakaoCallback />} />
                        <Route path="/login/callback/apple" element={<AppleCallback />} />
                        <Route path="/terms-of-service" element={<TermsOfService />} />
                        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                        <Route path="/refund-policy" element={<RefundPolicy />} />

                        {/* ===== 급여 실태조사: 랜딩·공개 리포트는 비로그인도 열람 ===== */}
                        <Route path="/salary-surveys" element={<SalarySurveyLandingPage />} />
                        <Route path="/salary-surveys/reports" element={<SalaryReportPage />} />
                        <Route path="/salary-surveys/reports/:key" element={<SalaryReportPage />} />

                        {/* ===== 공개 상세: 공유 URL로 비회원도 열람 가능 (목록·작성·액션은 로그인 필요) ===== */}
                        <Route path="/recruitments/:id" element={<RecruitmentsDetail />} />
                        <Route path="/notices/:noticeId" element={<NoticeDetail />} />
                        <Route path="/posts/:postId" element={<PostDetail />} />
                        {/* 대댓글 상세(폰) — 댓글 하나와 그 답글 */}
                        <Route path="/posts/:postId/comments/:commentId" element={<PostDetail />} />
                        <Route path="/expert-lounge/:id" element={<ExpertLoungeDetail />} />
                        <Route path="/expert-lounge/:id/comments/:commentId" element={<ExpertLoungeDetail />} />
                        <Route path="/seminars/:id" element={<SeminarDetail />} />

                        {/* ===== 로그인 필수: 홈 외 모든 화면 ===== */}
                        <Route path="/recruitments/region" element={<ProtectedRoute><RegionRecruitments /></ProtectedRoute>} />
                        <Route path="/recruitments/welfare" element={<ProtectedRoute><WelfareRecruitmentList /></ProtectedRoute>} />
                        <Route path="/recruitments/new" element={<ProtectedRoute><RecruitmentCreation /></ProtectedRoute>} />
                        <Route path="/recruitments/:id/applications" element={<ProtectedRoute><ApplicationManagementPage /></ProtectedRoute>} />
                        <Route path="/recruitments/:id/survey" element={<ProtectedRoute><RecruitmentSurveyPage /></ProtectedRoute>} />
                        <Route path="/recruitments/:recruitmentId/applications/:applicationId" element={<ProtectedRoute><ApplicationResumeView /></ProtectedRoute>} />
                        <Route path="/profile" element={<ProtectedRoute><MyPage /></ProtectedRoute>} />
                        <Route path="/profile/clinic/edit" element={<ProtectedRoute><ClinicInfoEditPage /></ProtectedRoute>} />
                        <Route path="/profile/user/edit" element={<ProtectedRoute><UserInfoEditPage /></ProtectedRoute>} />
                        <Route path="/profile/notifications" element={<ProtectedRoute><NotificationSettingsPage /></ProtectedRoute>} />
                        <Route path="/mypage/promise-record" element={<ProtectedRoute><PromiseRecordRedirect /></ProtectedRoute>} />
                        <Route path="/profile/license-verification" element={<ProtectedRoute><LicenseVerificationPage /></ProtectedRoute>} />
                        <Route path="/salary-surveys/response" element={<ProtectedRoute><SalarySurveyFormPage /></ProtectedRoute>} />
                        <Route path="/salary-surveys/response/submitted" element={<ProtectedRoute><SalarySurveySubmittedPage /></ProtectedRoute>} />
                        <Route path="/salary-surveys/results/me" element={<ProtectedRoute><SalarySurveyResultPage /></ProtectedRoute>} />
                        <Route path="/clinic/recruitments" element={<ProtectedRoute><ClinicRecruitmentsPage /></ProtectedRoute>} />
                        {/* 자주 묻는 질문 탭은 로그인 없이 — 문의 자동 답·FAQ 링크로 들어온다(공지 탭은 그대로 로그인 필요) */}
                        <Route path="/notices" element={<NoticeRoute />} />
                        <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
                        <Route path="/posts" element={<ProtectedRoute><Post /></ProtectedRoute>} />
                        <Route path="/posts/my" element={<ProtectedRoute><MyPosts /></ProtectedRoute>} />
                        <Route path="/posts/new" element={<ProtectedRoute><PostForm /></ProtectedRoute>} />
                        <Route path="/posts/:postId/edit" element={<ProtectedRoute><PostForm /></ProtectedRoute>} />
                        {/* 전문가 QnA는 전문가 라운지로 통합됨 — 옛 링크(공유·배너)는 리다이렉트 */}
                        <Route path="/expert-qna" element={<Navigate to="/expert-lounge" replace />} />
                        <Route path="/expert-qna/*" element={<Navigate to="/expert-lounge" replace />} />
                        <Route path="/expert-lounge" element={<ProtectedRoute><ExpertLounge /></ProtectedRoute>} />
                        <Route path="/expert-lounge/new" element={<ProtectedRoute><ExpertLoungeForm /></ProtectedRoute>} />
                        <Route path="/expert-lounge/:id/edit" element={<ProtectedRoute><ExpertLoungeForm /></ProtectedRoute>} />
                        <Route path="/seminars" element={<ProtectedRoute><SeminarList /></ProtectedRoute>} />
                        <Route path="/seminars/new" element={<ProtectedRoute><SeminarForm /></ProtectedRoute>} />
                        <Route path="/seminars/:id/edit" element={<ProtectedRoute><SeminarForm /></ProtectedRoute>} />
                        <Route path="/seminars/history" element={<ProtectedRoute><SeminarHistory /></ProtectedRoute>} />
                        <Route path="/payments/history" element={<ProtectedRoute><SeminarPaymentFeatureRoute gate="history"><PaymentHistoryPage /></SeminarPaymentFeatureRoute></ProtectedRoute>} />
                        <Route path="/coupons" element={<ProtectedRoute><SeminarPaymentFeatureRoute><MyCouponsPage /></SeminarPaymentFeatureRoute></ProtectedRoute>} />
                        <Route path="/seminars/:seminarId/participants" element={<ProtectedRoute><SeminarParticipants /></ProtectedRoute>} />
                        <Route path="/seminars/:id/payment/success" element={<ProtectedRoute><SeminarPaymentCallback mode="success" /></ProtectedRoute>} />
                        <Route path="/seminars/:id/payment/fail" element={<ProtectedRoute><SeminarPaymentCallback mode="fail" /></ProtectedRoute>} />
                        <Route path="/seminar/instructor/apply" element={<ProtectedRoute><InstructorApplication /></ProtectedRoute>} />
                        <Route path="/seminar/instructor/edit" element={<ProtectedRoute><InstructorEdit /></ProtectedRoute>} />
                        <Route path="/seminar/instructor/settlements" element={<ProtectedRoute><SeminarPaymentFeatureRoute gate="settlement"><InstructorSettlementPage /></SeminarPaymentFeatureRoute></ProtectedRoute>} />
                        <Route path="/instructors/:id" element={<ProtectedRoute><InstructorDetail /></ProtectedRoute>} />
                        <Route path="/apply/history" element={<ProtectedRoute><ApplyHistoryPage /></ProtectedRoute>} />
                        <Route path="/applications/:applicationId/thread" element={<ProtectedRoute><ApplicationThreadPage /></ProtectedRoute>} />
                        <Route path="/threads" element={<ProtectedRoute><ThreadListPage /></ProtectedRoute>} />
                        <Route path="/resume" element={<ProtectedRoute><RegisterResume /></ProtectedRoute>} />
                        <Route path="/resume/view" element={<ProtectedRoute><ResumeViewPage /></ProtectedRoute>} />
                        <Route path="/resume/edit" element={<ProtectedRoute><ResumeEditPage /></ProtectedRoute>} />
                        <Route path="/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
                        <Route path="/products/:category" element={<ProtectedRoute><PointFeatureRoute><ProductPurchasePage /></PointFeatureRoute></ProtectedRoute>} />
                        <Route path="/purchase-history" element={<ProtectedRoute><PointFeatureRoute><PurchaseHistoryPage /></PointFeatureRoute></ProtectedRoute>} />
                        <Route path="/favorites/clinics" element={<ProtectedRoute><FavoriteClinicsPage /></ProtectedRoute>} />
                        <Route path="/inquiry" element={<ProtectedRoute><InquiryFormPage /></ProtectedRoute>} />
                        {/* 이벤트 선물 받기(당첨자 본인) — docs/event-prize-plan.md 5장 */}
                        <Route path="/events/prize" element={<ProtectedRoute><PrizePage /></ProtectedRoute>} />
                        <Route path="/inquiries/my" element={<ProtectedRoute><MyInquiriesPage /></ProtectedRoute>} />
                        <Route path="/inquiries/:inquiryId" element={<ProtectedRoute><InquiryThreadPage /></ProtectedRoute>} />
                        <Route path="/points/charge" element={<ProtectedRoute><PointFeatureRoute><PointChargePage /></PointFeatureRoute></ProtectedRoute>} />
                        {/* AI 질문·답변 — 주소로 바로 들어온 경우만 여기로 온다: 폰은 홈 위 시트, PC 는 홈의 작은 창으로 옮긴다(AiChatPage).
                            공유받은 대화(/ai/s/:token)는 비회원도 본다 */}
                        <Route path="/ai" element={<ProtectedRoute><AiChatPage /></ProtectedRoute>} />
                        <Route path="/ai/history" element={<ProtectedRoute><AiChatPage /></ProtectedRoute>} />
                        <Route path="/ai/s/:token" element={<AiChatPage />} />
                        <Route path="/ai/:id" element={<ProtectedRoute><AiChatPage /></ProtectedRoute>} />

                        {/* 개발 전용: PaymentNavigation 결제모드 수동 검증 라우트. 운영 번들에는 포함되지 않는다. */}
                        {import.meta.env.DEV && (
                            <Route path="/__native-nav-test" element={<NativeNavTest />} />
                        )}

                        {/* 없는 주소로 들어오면 빈 화면만 보였다 — 무슨 일이 일어났는지 알리고 돌아갈 길을 준다 */}
                        <Route
                            path="*"
                            element={
                                <PageState
                                    title="페이지를 찾을 수 없습니다"
                                    description="주소가 바뀌었거나 삭제된 페이지일 수 있어요."
                                    action={{ label: '홈으로', to: '/' }}
                                    secondaryAction={{ label: '채용정보 보기', to: '/recruitments/region' }}
                                />
                            }
                        />
                    </Routes>
                    </Suspense>
                    <PageBannerSlot placement="bottom" variant="app" />
                </Container>
                <Footer />
                {/* 폰 AI 시트 — 지금 화면 위로 올라온다 */}
                <AiSheetHost />
                <Toast />
                {/* 공유 링크를 모바일 브라우저로 열었을 때 "앱으로 보기" 안내 (앱 안에서는 렌더되지 않음) */}
                <OpenInAppSheet />
            </ThemeProvider>
        </>
    );
}

function App() {
    return (
        <BrowserRouter>
            <PageViewTracker />
            <AppContent />
        </BrowserRouter>
    );
}

export default App;
